import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { categories } from '@/constants/categories';
import axios from 'axios';
import { API } from '@/config';
import { logError, checkApiHealth } from '@/utils/debug';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

// Import extracted components
import {
  MapLegend,
  MapHeader,
  FacebookBanner,
  WelcomeModal,
  ReportDialog,
  BoxPopupContent,
  DraggablePopupContent,
  LoadingState,
  EmptyState
} from '@/components/map';

// Import custom hooks
import usePopupInteractions from '@/hooks/usePopupInteractions';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Helper component to access map instance
const MapController = ({ onMapReady }) => {
  const map = useMap();
  useEffect(() => {
    onMapReady(map);
  }, [map, onMapReady]);
  return null;
};

// Sunshine Coast coordinates
const CENTER_POSITION = [-26.6500, 153.0667];

const MapView = () => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  
  // Core state
  const [boxes, setBoxes] = useState([]);
  const [filteredBoxes, setFilteredBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isLocationSearch, setIsLocationSearch] = useState(false);
  
  // UI state
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showFacebookBanner, setShowFacebookBanner] = useState(false);
  const [locatingUser, setLocatingUser] = useState(false);
  
  // Report dialog state
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportingBox, setReportingBox] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reportEmail, setReportEmail] = useState('');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  
  // Claim state
  const [isClaimingBox, setIsClaimingBox] = useState(false);
  
  // Popup interactions hook
  const { setupPopupInteractions } = usePopupInteractions();

  // Initialize app
  useEffect(() => {
    checkApiHealth();
    
    // Load user from localStorage
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        console.error("Failed to parse user from local storage", e);
      }
    }
    
    fetchBoxes();
    
    // Check if first visit
    const hasVisited = localStorage.getItem('np_visited');
    if (!hasVisited) {
      setShowWelcomeModal(true);
      localStorage.setItem('np_visited', 'true');
    }
    
    // Show Facebook banner after 5 seconds if not dismissed
    const fbBannerDismissed = localStorage.getItem('np_fb_banner_dismissed');
    if (!fbBannerDismissed) {
      const timer = setTimeout(() => setShowFacebookBanner(true), 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Filter boxes when dependencies change
  useEffect(() => {
    filterBoxes();
  }, [boxes, searchQuery, selectedCategory, isLocationSearch]);

  // Fetch boxes from API
  const fetchBoxes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/boxes`);
      setBoxes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logError('fetchBoxes', error);
      toast.error('Failed to load honesty boxes');
      setBoxes([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter boxes based on search and category
  const filterBoxes = () => {
    if (!Array.isArray(boxes)) return;

    let filtered = [...boxes];

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(box => box.category === selectedCategory);
    }

    // Only apply text filtering if NOT doing a location search
    if (searchQuery && !isLocationSearch) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(box =>
        (box.name && box.name.toLowerCase().includes(q)) ||
        (box.description && box.description.toLowerCase().includes(q)) ||
        (box.address && box.address.toLowerCase().includes(q))
      );
    }

    setFilteredBoxes(filtered);
  };

  // Handle search submission (geocoding)
  const handleSearchSubmit = async (e) => {
    if (e.key === 'Enter' && searchQuery.length >= 3) {
      setIsLocationSearch(true);
      
      try {
        const response = await axios.post(`${API}/geocode`, {
          address: searchQuery,
          region: 'AU'
        });

        if (response.data.success) {
          const { latitude, longitude, locality } = response.data;
          
          if (mapRef.current) {
            mapRef.current.flyTo([latitude, longitude], 14, { duration: 0.8 });
            toast.success(`Zooming to ${locality || searchQuery}`);
          }
        } else {
          toast.error('Location not found. Try a different search.');
        }
      } catch (error) {
        console.error('Geocoding error:', error);
        toast.error('Could not find that location');
      } finally {
        setSearchQuery('');
        setIsLocationSearch(false);
      }
    }
  };

  // Handle get directions
  const handleGetDirections = (provider, box) => {
    const dest = box.address 
      ? encodeURIComponent(box.address) 
      : (box.lat && box.lng ? `${box.lat},${box.lng}` : null);

    if (!dest) {
      toast.error('Location details missing');
      return;
    }

    const urls = {
      google: `https://www.google.com/maps/dir/?api=1&destination=${dest}`,
      apple: `https://maps.apple.com/?daddr=${dest}`,
      waze: `https://waze.com/ul?q=${dest}&navigate=yes`,
      osm: `https://www.openstreetmap.org/directions?to=${dest}`
    };
    
    if (urls[provider]) {
      window.open(urls[provider], '_blank', 'noopener,noreferrer');
    }
  };

  // Find boxes near user's location
  const handleFindNearby = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    
    setLocatingUser(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (mapRef.current) {
          mapRef.current.flyTo([latitude, longitude], 14, { duration: 1 });
          toast.success('Showing boxes near you!');
        }
        setLocatingUser(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error('Could not get your location. Please enable location services.');
        setLocatingUser(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Share box to Facebook
  const handleShareToFacebook = (box) => {
    const shareText = `Check out "${box.name}" on Neighbours Pantry! 🥬🍎\n\n${box.description}\n\n📍 ${box.address}`;
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}&quote=${encodeURIComponent(shareText)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  // Claim an unclaimed box
  const handleClaimBox = async (box) => {
    if (!user) {
      toast.error('Please log in to claim this stall');
      navigate('/vendor/login');
      return;
    }
    
    setIsClaimingBox(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/boxes/${box.id}/claim`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Successfully claimed this stall! You can now manage it from your dashboard.');
      fetchBoxes();
    } catch (error) {
      logError('claimBox', error);
      const message = error.response?.data?.detail || 'Failed to claim this stall';
      toast.error(message);
    } finally {
      setIsClaimingBox(false);
    }
  };

  // Open report dialog
  const openReportDialog = (box) => {
    setReportingBox(box);
    setReportReason('');
    setReportEmail('');
    setReportDialogOpen(true);
  };

  // Submit report
  const handleSubmitReport = async () => {
    if (!reportReason.trim()) {
      toast.error('Please provide a reason for reporting');
      return;
    }
    
    setIsSubmittingReport(true);
    try {
      await axios.post(`${API}/boxes/${reportingBox.id}/request-removal-public`, {
        reason: reportReason,
        contact_email: reportEmail || null
      });
      
      toast.success('Report submitted successfully. An admin will review it shortly.');
      setReportDialogOpen(false);
      setReportingBox(null);
    } catch (error) {
      logError('submitReport', error);
      const message = error.response?.data?.detail || 'Failed to submit report';
      toast.error(message);
    } finally {
      setIsSubmittingReport(false);
    }
  };

  // Dismiss Facebook banner
  const dismissFacebookBanner = () => {
    setShowFacebookBanner(false);
    localStorage.setItem('np_fb_banner_dismissed', 'true');
  };

  // Create custom marker icon
  const createCustomIcon = (category, isClaimed = true) => {
    const categoryData = categories.find(cat => cat.value === category) || categories[0];
    const iconContent = categoryData.label ? categoryData.label.charAt(0).toUpperCase() : '📍';
    
    const bgColor = isClaimed ? categoryData.color : '#f59e0b';
    const borderStyle = isClaimed ? '3px solid white' : '3px dashed #fbbf24';
    
    return L.divIcon({
      html: `<div style="background-color: ${bgColor}; width: 32px; height: 32px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: ${borderStyle}; box-shadow: 0 4px 12px rgba(0,0,0,0.2); display: flex; align-items: center; justify-content: center;"><div style="transform: rotate(45deg); color: white; font-weight: bold; font-size: 14px;">${isClaimed ? iconContent : '?'}</div></div>`,
      className: '',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32],
    });
  };

  return (
    <div className="relative w-full h-screen">
      {/* Facebook Community Banner */}
      <FacebookBanner 
        show={showFacebookBanner} 
        onDismiss={dismissFacebookBanner} 
      />

      {/* Header */}
      <MapHeader
        showFacebookBanner={showFacebookBanner}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        handleSearchSubmit={handleSearchSubmit}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        filteredBoxes={filteredBoxes}
        boxes={boxes}
        user={user}
        navigate={navigate}
        handleFindNearby={handleFindNearby}
        locatingUser={locatingUser}
      />

      {/* Map Container */}
      <div className={`w-full h-full pb-[80px] ${showFacebookBanner ? 'pt-[320px] md:pt-[260px]' : 'pt-[280px] md:pt-[220px]'}`}>
        {loading ? (
          <LoadingState />
        ) : boxes.length === 0 ? (
          <EmptyState navigate={navigate} />
        ) : (
          <MapContainer
            center={CENTER_POSITION}
            zoom={11}
            style={{ height: '100%', width: '100%' }}
            data-testid="map-container"
          >
            <MapController onMapReady={(map) => { mapRef.current = map; }} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {/* Markers */}
            {filteredBoxes.map((box) => (
              <Marker
                key={box.id}
                position={[box.lat, box.lng]}
                icon={createCustomIcon(box.category, box.is_claimed !== false)}
                data-testid={`marker-${box.id}`}
              >
                <Popup maxWidth={320} autoPan={true}>
                  <DraggablePopupContent onMount={setupPopupInteractions}>
                    <BoxPopupContent
                      box={box}
                      user={user}
                      isClaimingBox={isClaimingBox}
                      onGetDirections={handleGetDirections}
                      onShareToFacebook={handleShareToFacebook}
                      onClaimBox={handleClaimBox}
                      onReportBox={openReportDialog}
                    />
                  </DraggablePopupContent>
                </Popup>
              </Marker>
            ))}
            
            {/* Map Legend */}
            <MapLegend />
          </MapContainer>
        )}
      </div>

      {/* Footer */}
      <footer className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md shadow-lg py-4 z-[1000]">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-gray-600">
            © {new Date().getFullYear()} Neighbours Pantry • Sunshine Coast Community
            <a 
              href="https://www.facebook.com/groups/1483887879720908" 
              target="_blank" 
              rel="noopener noreferrer"
              className="ml-2 inline-flex items-center text-blue-600 hover:text-blue-700"
            >
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Join Community
            </a>
          </p>
        </div>
      </footer>

      {/* Welcome Modal */}
      <WelcomeModal
        open={showWelcomeModal}
        onOpenChange={setShowWelcomeModal}
        navigate={navigate}
      />

      {/* Report Dialog */}
      <ReportDialog
        open={reportDialogOpen}
        onOpenChange={setReportDialogOpen}
        reportingBox={reportingBox}
        reportReason={reportReason}
        setReportReason={setReportReason}
        reportEmail={reportEmail}
        setReportEmail={setReportEmail}
        isSubmitting={isSubmittingReport}
        onSubmit={handleSubmitReport}
      />
    </div>
  );
};

export default MapView;
